// Epic 8 follow-on — mandatory creator SIGNATURE upload.
//
// Sits directly under the creator-photo ImageDropzone on the apply form.
// Mirrors ImageDropzone's mint → PUT → preview flow but:
//   * accepts PNG/WebP (dark ink on white/transparent; PNG preferred),
//   * mints via the signature-specific route,
//   * renders ONE preview chip (the live cert preview beside the form
//     shows how the ink reads on the parchment) with a hover (×) to
//     remove the upload.
//
// The backend white-keys + validates the signature at application SUBMIT;
// this component only handles the upload + local preview.

import { useRef, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "@greenhold/components";

import { useMintSignatureUploadUrl } from "./useMintSignatureUploadUrl";

const MAX_BYTES = 2 * 1024 * 1024;

// The certificate renders signatures at a fixed ink height inside a
// 150×34 max box (see PatronageCertificate INK_H/INK_MAX_W). Reject
// uploads whose aspect ratio that box can't render sensibly: portrait
// images and extremely long strips. ~3:1 wide is the sweet spot.
const MIN_ASPECT = 1;
const MAX_ASPECT = 8;

interface Props {
  onUploaded: (blobPath: string, previewUrl: string) => void;
  onRemoved: () => void;
  blobPath: string | null;
  previewUrl: string | null;
}

type UploadState =
  | { kind: "idle" }
  | { kind: "validating" }
  | { kind: "minting" }
  | { kind: "uploading" }
  | { kind: "done" }
  | { kind: "error"; message: string };

export function SignatureDropzone({
  onUploaded,
  onRemoved,
  blobPath,
  previewUrl,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mintMutation = useMintSignatureUploadUrl();
  const [state, setState] = useState<UploadState>(
    blobPath ? { kind: "done" } : { kind: "idle" },
  );
  const [dragging, setDragging] = useState(false);

  const validateAndUpload = async (file: File) => {
    setState({ kind: "validating" });

    if (!["image/png", "image/webp"].includes(file.type)) {
      setState({
        kind: "error",
        message: "Signature must be a PNG or WebP image.",
      });
      return;
    }
    if (file.size === 0) {
      setState({ kind: "error", message: "File is empty." });
      return;
    }
    if (file.size > MAX_BYTES) {
      setState({ kind: "error", message: "File is larger than 2 MB." });
      return;
    }

    // Aspect-ratio check via FileReader + Image() (same pattern as the
    // photo dropzone's dimension check).
    const dims = await new Promise<{ w: number; h: number } | null>(
      (resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.width, h: img.height });
          img.onerror = () => resolve(null);
          img.src = e.target?.result as string;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      },
    );
    if (!dims) {
      setState({
        kind: "error",
        message: "Couldn't read the image. Try a different file.",
      });
      return;
    }
    const aspect = dims.w / dims.h;
    if (aspect < MIN_ASPECT) {
      setState({
        kind: "error",
        message:
          "Signature image is taller than it is wide — crop it to the ink's bounding box (roughly 3:1 wide works best).",
      });
      return;
    }
    if (aspect > MAX_ASPECT) {
      setState({
        kind: "error",
        message:
          "Signature image is a very long strip — crop it closer to the ink (roughly 3:1 wide works best).",
      });
      return;
    }

    await mintAndPut(file, false);
  };

  const mintAndPut = async (file: File, isRetry: boolean) => {
    setState({ kind: "minting" });
    let minted;
    try {
      minted = await mintMutation.mutateAsync({
        filename: file.name,
        content_length: file.size,
        content_type: file.type,
      });
    } catch (err) {
      const detail = err as { message?: string };
      setState({
        kind: "error",
        message: detail.message ?? "Failed to mint upload URL.",
      });
      return;
    }

    setState({ kind: "uploading" });
    try {
      const putResp = await fetch(minted.upload_url, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": minted.required_headers["x-ms-blob-type"],
          "x-ms-blob-content-type":
            minted.required_headers["x-ms-blob-content-type"],
        },
        body: file,
      });
      if (!putResp.ok) {
        if (putResp.status === 403 && !isRetry) {
          await mintAndPut(file, true);
          return;
        }
        setState({
          kind: "error",
          message: `Upload failed (HTTP ${putResp.status}). Try again.`,
        });
        return;
      }
    } catch {
      setState({
        kind: "error",
        message: "Upload network error. Check your connection and retry.",
      });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    onUploaded(minted.blob_path, objectUrl);
    setState({ kind: "done" });
    toast.success("Signature uploaded");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void validateAndUpload(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void validateAndUpload(file);
    }
  };

  const handleRemove = () => {
    // Reset the input value so re-selecting the same file fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
    setState({ kind: "idle" });
    onRemoved();
  };

  const statusLabel = (() => {
    switch (state.kind) {
      case "validating":
        return "Validating signature…";
      case "minting":
        return "Preparing upload…";
      case "uploading":
        return "Uploading…";
      case "done":
        return "Signature uploaded ✓";
      case "error":
        return state.message;
      default:
        return null;
    }
  })();
  const isBusy =
    state.kind === "validating" ||
    state.kind === "minting" ||
    state.kind === "uploading";

  return (
    <div className="space-y-2">
      <label
        htmlFor="creator-signature-input"
        className="block text-sm font-semibold"
      >
        Signature *
      </label>
      <p className="text-sm opacity-70">
        Dark ink on a white or transparent background, cropped close to the
        ink (wider than tall — roughly 3:1). PNG preferred, up to 2 MB.
        We&apos;ll clean the background so it engraves onto your certificates.
      </p>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        className={[
          "rounded-md border-2 border-dashed p-6 text-center transition-colors",
          dragging
            ? "border-brand-gold-500 bg-brand-paper-200"
            : "border-brand-forest-200 bg-brand-paper-50",
        ].join(" ")}
      >
        {previewUrl ? (
          <div className="flex flex-col items-center gap-3">
            {/* Single chip for the one uploaded item; the live cert preview
                shows how the ink reads on the parchment. Hover reveals (×). */}
            <div className="group relative">
              <div className="flex h-20 w-40 items-center justify-center rounded-md border border-brand-forest-200 bg-white p-2">
                <img
                  src={previewUrl}
                  alt="Uploaded signature"
                  data-testid="signature-preview"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <button
                type="button"
                aria-label="Remove signature"
                onClick={handleRemove}
                disabled={isBusy}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-brand-forest-200 bg-white text-sm leading-none shadow-sm opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:bg-brand-paper-200"
              >
                ×
              </button>
            </div>
            <Button
              type="button"
              variant="tertiary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              Replace signature
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="opacity-80">Drag a file here or</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              Choose a file
            </Button>
          </div>
        )}
        <input
          ref={fileInputRef}
          id="creator-signature-input"
          type="file"
          accept="image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      {statusLabel && (
        <p
          className={
            state.kind === "error"
              ? "text-sm text-destructive"
              : "text-sm opacity-70"
          }
        >
          {statusLabel}
        </p>
      )}
    </div>
  );
}
